import { capfirst, intcomma } from 'journalize';
import cx from 'classnames';
import { Component, h } from 'preact';
import { PreactHTMLConverter } from 'preact-html-converter';

// internal
import find from './utils/find';
import {
  sort_candidates_by_party,
  get_directory_slug,
} from '../election-utils';
import OtherCandidate from './OtherCandidate';
import sumValues from '../utils/sum-values';

// text
import text from '../../../data/text.json';

const converter = PreactHTMLConverter();

type Props = {
  contest: { [key: string]: any },
  columnCount: number,
  annotations: { [key: string]: any },
  tableHeader: JSX.Element;
  contestTitle: string;
  renderCandidate: any,
  visibleCandidatesCount?: number,
  embedType: string,
  key: string,
  activeContestId: string
};

class Contest extends Component<Props> {
  constructor() {
    super();

    this.state = {
      viewAllCandidates: false,
    };
    this.onViewableToggle = this.onViewableToggle.bind(this);
  }

  onViewableToggle() {
    this.setState(prevState => ({
      viewAllCandidates: !prevState.viewAllCandidates, // set viewAllCandidates to what is wasn't before
    }));
  }

  render() {
    const {
      contest,
      annotations,
      columnCount,
      visibleCandidatesCount,
      contestTitle,
      renderCandidate,
      tableHeader,
      embedType,
    } = this.props;

    const { viewAllCandidates } = this.state;

    const shouldDisplayResult = contest.contest_result !== 'not called';

    const totalVotes = sumValues(contest.candidacies, 'votes');

    let visibleCandidates = visibleCandidatesCount;

    const IsAnnotations =
      annotations && annotations.length > 0 && annotations[0].value != ''
        ? true
        : false;

    const isSpecial =
        contest.election_type == 'special'
          ? true
          : false;

    // The Republic API slugs don't match the directory url slugs so we need to get them

    let directory_slug;

    if (contest) {
      directory_slug = get_directory_slug(contest.office);
    }

    let partyName = contest.party;

    const rowClass = cx('seat__race', {
      'seat__race--Democrat': partyName == 'Democrat',
      'seat__race--Republican': partyName == 'Republican',
    });



    const contestCalledClass = cx('contest__called-row', {
      'dem-winner-banner': partyName == 'Democrat',
      'rep-winner-banner': partyName == 'Republican',
      'special-winner-banner': contest.election_type == 'special',
    });

    const contestResults =
      contest.contest_result == 'runoff'
        ? `Will continue to ${contest.contest_result}`
        : contest.contest_result == 'winner'
        ? `Apparent ${contest.contest_result}`
        : `${contest.contest_result}`;

    let candidates = contest.candidacies;
    
    let embedTypeUsable = 'none'
    
    if (typeof embedType !== 'undefined') {
      embedTypeUsable = embedType;
    }

    // set the class on the disclaimer, changes between partner embed and the default index page
    const isPartner = embedTypeUsable && embedTypeUsable == 'partner' ? true : false;


    const disclaimerClass = cx(
      'disclaimer-race',
      {
        'disclaimer-partner': isPartner
      } // disclaimer-partner needs to be after disclaimer-race
    );

    return (
      <div class="seat">
        <div class={rowClass}>
          <div class="race">
            <h3 class="seat__title">
              {/* The contest title and the little icon next to it that links to our directory if applicable */}
              <span>{contestTitle}</span>
              {directory_slug[0] != null && (
                <a
                  href={`https://www.texastribune.org/directory/districts/${directory_slug[0]['directory-slug']}/${contest['office_division']}`}
                >
                  <span class="icon-div">
                    <span class="is-sr-only">
                      Click to learn more about this district
                    </span>
                    <svg class="icon icon-info" aria-hidden="true">
                      <use xlinkHref="#icon-info"></use>
                    </svg>
                  </span>
                </a>
              )}
            </h3>
            <h4 class="race__title">
              {partyName == 'Democrat' && (
                <span>{partyName}ic primary runoff</span>
              )}
              {partyName == 'Republican' && (
                <span>{partyName} primary runoff</span>
              )}
            </h4>

            {/* {isSpecial == true && (
              <p class="copy">
                {converter.convert(text.special_election_note[0].value)}
              </p>
            )} */}

            <table class="race__candidates">
              {tableHeader}
              <tbody>
                {shouldDisplayResult && (
                  <tr class={contestCalledClass}>
                    <td colSpan={columnCount}>{contestResults}</td>
                  </tr>
                )}
                {candidates.map((candidate: any, idx: any) =>
                  renderCandidate(candidate, idx, viewAllCandidates, {
                    totalVotes,
                  })
                )}
                {!embedTypeUsable.includes('broadcast') &&
                  candidates.length > visibleCandidates && (
                    <OtherCandidate
                      isViewable={!viewAllCandidates}
                      candidateCount={candidates.length}
                      embedType={embedType}
                      onClick={() => {
                        this.onViewableToggle();
                      }}
                    />
                  )}
              </tbody>

              {candidates.length > visibleCandidates && viewAllCandidates && (
                <tr class="candidate race__candidate-row">
                  <td
                    class="candidate__cell candidate__party t-align-center"
                    onClick={() => {
                      this.onViewableToggle();
                    }}
                  >
                    <span class="is-sr-only">
                      Click here to see more candidates
                    </span>
                    <svg class="icon icon-angle-up" aria-hidden="true">
                      <use xlinkHref="#icon-angle-up" />
                    </svg>
                  </td>
                  <th class="candidate__cell candidate__name">
                    <button
                      class={embedType}
                      onClick={() => {
                        this.onViewableToggle();
                      }}
                    >
                      Show fewer ({visibleCandidates})
                    </button>
                  </th>
                  <th class="candidate__cell candidate__votes"> </th>
                  <th class="candidate__cell candidate__pct"></th>
                </tr>
              )}
            </table>
            {/* {isSpecial != true && (
              <div class="countdown">
                Updating <span class="counter"></span>
              </div>
            )} */}
            <div class="contest__metrics" style="padding-top: 3px;">
              {isSpecial != true && (
                <p class="contest__precincts">
                  {`${intcomma(
                    contest.pollinglocationsreporting
                  )} of ${intcomma(
                    contest.total_pollinglocations
                  )} total polling locations reporting (${contest.pollinglocations_reporting_percent.toFixed(
                    1
                  )}%)`}
                </p>
              )}
            </div>
            {/* Added text to explain total polling location caveats*/}
            {/* <p class="contest__precincts" style="padding-top: 3px;">
              *Not all mail-in ballots are included.
            </p> */}
            <p class="contest__precincts" style="padding-top: 3px;">
              Source: Texas Secretary of State
      </p>

            {/* {isSpecial == false && (
              <p class="contest__precincts" style="padding-top: 3px;">
              Source: Texas Secretary of State
            </p>
            )} */}
          </div>
        </div>

        {IsAnnotations == true && (
          <div class={disclaimerClass}>
            <svg class="icon note-icon anno-div" aria-hidden="true">
              <use xlinkHref="#note-icon" />
            </svg>
            <div class="anno-text">
              {converter.convert(annotations[0].value)}
            </div>
          </div>
        )}
      </div>
    );
  }
}

Contest.defaultProps = {
  visibleCandidatesCount: 3,
};

export default Contest;